import React from 'react';
import PropTypes from 'prop-types';
import { MdInfoOutline, MdOpenInNew } from 'react-icons/md';
import { Modal, Button } from 'components/common';
import GoogleMap from 'components/common/map/GoogleMap';

/**
 * Approximate location of a sign-in IP.
 *
 * The disclaimer is not decoration. IP geolocation resolves to the ISP's
 * registered address — routinely the wrong district, often the wrong city, and
 * meaningless behind a VPN or on mobile data. On a security screen, a pin
 * presented without that caveat invites someone to accuse a colleague of signing
 * in from somewhere they have never been.
 */
const SessionLocationModal = ({ session, onClose, remoteEnabled = false }) => {
  if (!session) return null;

  const center = { lat: Number(session.latitude), lng: Number(session.longitude) };
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${center.lat},${center.lng}`;

  return (
    <Modal
      show
      onHide={onClose}
      size="lg"
      title={`Sign-in location · ${session.ip_address}`}
      footer={(
        <>
          <Button size="sm" variant="secondary" onClick={() => window.open(mapsUrl, '_blank', 'noopener')}>
            <MdOpenInNew size={14} /> Open in Google Maps
          </Button>
          <Button size="sm" onClick={onClose}>Close</Button>
        </>
      )}
    >
      <div className="tw-mb-3 tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-1 tw-text-sm">
        <span><span className="tw-text-slate-400">User: </span><strong>{session.user?.full_name || session.user?.name}</strong></span>
        <span><span className="tw-text-slate-400">Location: </span><strong>{session.location || '—'}</strong></span>
        <span><span className="tw-text-slate-400">Device: </span><strong>{session.device_label} · {session.os}</strong></span>
      </div>

      {/* GoogleMap is tw-h-full, so without an explicit height here it collapses
          to zero and the modal shows an empty box. */}
      <div className="tw-h-96 tw-rounded tw-overflow-hidden tw-border tw-border-solid tw-border-slate-200">
        <GoogleMap initialCenter={center} zoom={10} />
      </div>

      <p className="tw-flex tw-items-start tw-gap-2 tw-text-xs tw-text-amber-800 tw-bg-amber-50 tw-border tw-border-solid tw-border-amber-200 tw-rounded-md tw-px-3 tw-py-2 tw-mt-3 tw-mb-0">
        <MdInfoOutline size={15} className="tw-shrink-0 tw-mt-px" />
        Estimated from the IP address, accurate to city level at best. Mobile data
        and VPNs commonly resolve to another city entirely — treat this as a hint,
        not as evidence of where someone was.
      </p>

      {/* Credits whichever resolver is actually answering. When the online
          lookup is off and the DB-IP file answers, its CC BY 4.0 licence
          REQUIRES the credit — that branch is a licence term, not a courtesy. */}
      <p className="tw-text-[11px] tw-text-slate-400 tw-mt-2 tw-mb-0 tw-text-right">
        IP geolocation by{' '}
        {remoteEnabled ? (
          <a href="https://ipwho.is" target="_blank" rel="noopener noreferrer" className="tw-text-slate-400 hover:tw-underline">
            ipwho.is
          </a>
        ) : (
          <a href="https://db-ip.com" target="_blank" rel="noopener noreferrer" className="tw-text-slate-400 hover:tw-underline">
            DB-IP
          </a>
        )}
      </p>
    </Modal>
  );
};

SessionLocationModal.propTypes = {
  session: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  remoteEnabled: PropTypes.bool,
};

export default SessionLocationModal;
